import axios from 'axios';
import config from '../config';
import { showError } from '../utils';
import moment from 'moment';

import { store } from '../store';

// Generic network call function to handle common API request patterns
function networkCall({ method, url, headers, params, data, ...extraOptions }) {
  return axios({
    method,
    url,
    headers,
    params,
    data,
    ...extraOptions,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const error = new Error('Invalid response');
        error.data = responseBody;
        throw error;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function defaultIssueSearch(searchType, authToken, extraParams = {}) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/prereqs`,
    headers: { authorization: authToken },
    params: { t: searchType, ...extraParams },
  });
}

export function searchIssues(searchQuery, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/issues`,
    headers: { authorization: authToken },
    params: { q: searchQuery },
  });
}

export function getIllnessCategoryList(authToken, verticalId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/list-illness`,
    headers: { authorization: authToken },
    params: { verticalId },
  });
}

export function logIllness(authToken, illnessId) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/log-illness?illnessId=${illnessId}`,
    headers: { authorization: authToken },
    data: { illnessId },
  });
}

export function autoSuggest(
  searchQuery,
  searchType,
  lat,
  lng,
  authToken,
  prebookappt,
  extraParams = {}
) {
  const params = {
    q: searchQuery,
    t: searchType,
    lat,
    lng,
  };

  if (prebookappt) {
    params.prebooked = 1;
  }

  Object.assign(params, extraParams);

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/auto-suggest`,
    headers: { authorization: authToken },
    params,
  });
}

export function getRelatives(userId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/users/${userId}/relatives`,
    headers: { authorization: store.getState().user.authToken },
  });
}

export function addNewRelative(
  relationId,
  relativeName,
  relativeDob,
  relativePhone,
  userId,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/users/${userId}/relatives`,
    headers: { authorization: authToken },
    data: {
      relationId,
      relativeName,
      relativeDob,
      relativePhone,
    },
  });
}

export function searchAssistants(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/assistants`,
    headers: { authorization: authToken },
  });
}

export function searchOnlineDoctors(params, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/specialists/online`,
    headers: { authorization: authToken },
    params,
  });
}

export function getSlots(doctorId, authToken, hcData) {
  let url = `${config.apiBaseUrl}/opd/doctors/${doctorId || null}/slots`;

  if (!doctorId) url += `?centerRequest=1&v=${config.v2}`;

  return networkCall({
    method: 'get',
    url,
    headers: { authorization: authToken },
  });
}

export function requestAppointment(
  doctorId,
  consultationId,
  slots,
  latitude,
  longitude,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/request`,
    headers: { authorization: authToken },
    params: { v: config.version },
    data: {
      doctorId,
      consultationId,
      slots,
      latitude,
      longitude,
    },
  });
}

export function getRequestStatus(requestId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/status`,
    headers: { authorization: authToken },
  });
}

export function getPrebookDetails(doctorId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/prebook-details/${doctorId}`,
    headers: { authorization: authToken },
  });
}

export function getRequestStatusForBlocker(requestId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consult/offline/${requestId}/status?version=180000868`,
    headers: { authorization: authToken },
  });
}

export function cancelSlotRequest(
  requestId,
  amount,
  authToken,
  subCancelReason
) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/confirm`,
    headers: { authorization: authToken },
    params: {
      choice: 0,
      amount,
      subCancelReason,
    },
  });
}

export function rescheduleSlotRequest(
  requestId,
  dateKey,
  timingsid,
  authToken,
  preBooked,
  slotKey
) {
  const queryParams = new URLSearchParams();
  if (timingsid !== null) {
    queryParams.append('timingsid', timingsid);
  }
  if (preBooked === 1) {
    queryParams.append('preBooked', preBooked);
  }
  if (slotKey) {
    queryParams.append('slotKey', slotKey);
  }

  return networkCall({
    method: 'put',
    url: `${config.apiBaseUrl
      }/opd/v2/consult/request/${requestId}/reschedule-appointment?datekey=${dateKey}&${queryParams.toString()}`,
    headers: { authorization: authToken },
    data: null,
  });
}

export function confirmAlternateSlotRequest(
  requestId,
  po,
  dateKey,
  slotId,
  amount,
  authToken,
  history
) {
  const headers = {
    authorization: authToken,
  };
  const params = {
    choice: 2,
    dateKey,
    slotId,
    amount,
    po,
  };
  const redirectToPaymentGateway = () => {
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/request/${requestId}/confirm?choice=2&dateKey=${dateKey}&slotId=${slotId}&amount=${amount}&po=${po}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/assistant`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function confirmSlotRequest(requestId, po, amount, authToken, history) {
  const headers = {
    authorization: authToken,
  };
  const params = {
    choice: 1,
    amount,
    po,
  };
  const redirectToPaymentGateway = () => {
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/request/${requestId}/confirm?choice=1&amount=${amount}&po=${po}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/assistant`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function payForOfflineRequest(
  doctorInfo,
  consultRequestInfo,
  timingsId,
  dateKey,
  authToken,
  history,
  illnessId,
  timingsText,
  externalSlotId,
  externalAppointmentToken,
  externalUserId
) {
  const headers = {
    authorization: authToken,
  };
  let timingsEndText = null;
  if (timingsText && timingsText.length > 4) {
    const timingsSplit = timingsText.split('-');
    timingsText = moment(timingsSplit[0], 'HH:mm A').format('HH:mm:ss A');
    if (timingsSplit[1]) {
      timingsEndText = moment(timingsSplit[1], 'HH:mm A').format('HH:mm:ss A');
    }
  } else {
    timingsText = null;
  }
  const params = {
    doctorId: (doctorInfo || {}).doctorId,
    v: doctorInfo.verticalId,
    timingsId,
    dateKey,
    amount: (doctorInfo || {}).amount,
    illnessId,
    slotKey: timingsText,
    slotKeyEndTime: timingsEndText,
    externalSlotId,
    externalAppointmentToken,
    externalUserId,
  };
  if (consultRequestInfo) {
    const {
      selectedIssuesIndex,
      relative,
      consultationId,
      currentLat,
      currentLong,
    } = consultRequestInfo;
    if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
      params.issues = selectedIssuesIndex.toString();
    }
    if (
      relative &&
      relative.relativeId !== -1 &&
      relative.relativeId !== 'self'
    ) {
      params.relativeId =
        relative.relativeId !== -1 ? relative.relativeId : undefined;
    }
    if (consultationId) {
      params.consultationId = consultationId;
    }
    if (currentLat && currentLong && currentLat !== -1 && currentLong !== -1) {
      params.latitude = currentLat;
      params.longitude = currentLong;
    }
    if (illnessId) {
      params.illnessId = illnessId;
    }
  }
  const redirectToPaymentGateway = () => {
    const queryParams = new URLSearchParams();
    if (doctorInfo.doctorId) {
      queryParams.append('doctorId', doctorInfo.doctorId);
    }
    queryParams.append('amount', doctorInfo.finalFee);
    queryParams.append('v', doctorInfo.verticalId);
    if (consultRequestInfo) {
      const {
        selectedIssuesIndex,
        relative,
        consultationId,
        currentLat,
        currentLong,
      } = consultRequestInfo;
      if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
        queryParams.append('issues', selectedIssuesIndex.toString());
      }
      if (
        relative &&
        relative.relativeId !== -1 &&
        relative.relativeId !== 'self'
      ) {
        queryParams.append(
          'relativeId',
          relative.relativeId !== -1 ? relative.relativeId : undefined
        );
      }
      if (consultationId) {
        queryParams.append('consultationId', consultationId);
      }
      if (
        currentLat &&
        currentLong &&
        currentLat !== -1 &&
        currentLong !== -1
      ) {
        queryParams.append('latitude', currentLat);
        queryParams.append('longitude', currentLong);
      }
      queryParams.append('timingsId', timingsId);
      queryParams.append('dateKey', dateKey);
      queryParams.append('illnessId', illnessId);
      queryParams.append('slotKey', timingsText);
      queryParams.append('slotKeyEndTime', timingsEndText);
      if (externalSlotId) {
        queryParams.append('externalSlotId', externalSlotId);
      }
      if (externalAppointmentToken) {
        queryParams.append(
          'externalAppointmentToken',
          externalAppointmentToken
        );
      }
      if (externalUserId) {
        queryParams.append('externalUserId', externalUserId);
      }
    }
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/offline?${queryParams.toString()}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/offline`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function searchSpecialists(
  consultationType,
  issueIds,
  latitude,
  longitude,
  city,
  verticalId,
  healthCentreId,
  filtersParam,
  searchInput,
  authToken,
  nextToken,
  illnessId,
  prebookedId,
  selectedSortOption,
  extraParams = {}
) {
  const dynamicParams = {};
  if (selectedSortOption?.type === 'orderBy') {
    dynamicParams.orderBy = selectedSortOption.value; // Send orderBy with the selected value
    dynamicParams.sortBy = 'fee'; // Hardcoded to "fee" when type is orderBy
  } else if (selectedSortOption?.type === 'sortBy') {
    dynamicParams.sortBy = selectedSortOption.value; // Send sortBy with the selected value
  }

  if (extraParams) {
    Object.assign(dynamicParams, extraParams);
  }

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/pin-point`,
    headers: { authorization: authToken },
    params: {
      t: consultationType,
      i: issueIds.length === 0 ? undefined : issueIds.join(','),
      lat: latitude === -1 ? undefined : latitude,
      lng: longitude === -1 ? undefined : longitude,
      city,
      versionCode: config.v2,
      v: verticalId,
      hc: healthCentreId,
      f: filtersParam,
      q: searchInput.trim() === '' ? undefined : searchInput,
      next: nextToken,
      illnessId: illnessId ? illnessId : undefined,
      prebooked: prebookedId ? prebookedId : undefined,
      ...dynamicParams,
    },
  });
}

export function uploadImageAttachment(
  file,
  userId,
  consultationId,
  doctorId,
  onUploadProgress,
  authToken
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('consultationId', consultationId);
  formData.append('doctorId', doctorId);
  formData.append('userId', userId);
  formData.append('preview', file);

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/users/${userId}/upload`,
    headers: { authorization: authToken },
    data: formData,
    onUploadProgress,
  });
}

export function uploadFileAttachment(
  file,
  userId,
  consultationId,
  doctorId,
  prescription,
  onUploadProgress,
  authToken,
  reimbursementId,
  reimbursementType,
  invoice
) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('preview', file);
  formData.append('consultationId', consultationId);
  formData.append('doctorId', doctorId);
  formData.append('userId', userId);
  if (prescription) {
    formData.append('prescription', 1);
  }
  if (invoice) {
    formData.append('invoice', 1);
  }
  if (reimbursementId) {
    formData.append('reimbursementId', reimbursementId);
  }
  if (reimbursementType) {
    formData.append('reimbursementType', reimbursementType);
  }

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/users/${userId}/upload`,
    headers: { authorization: authToken },
    data: formData,
    onUploadProgress,
  });
}

/**
 * File upload for chat only upload
 * @param {*} file
 * @param {*} userId
 * @param {*} consultationId
 * @param {*} doctorId
 * @param {*} onUploadProgress
 * @param {*} authToken
 * @returns
 */
export function uploadImageAttachmentChatOnly(file, consultationId, authToken) {
  const formData = new FormData();
  formData.append('file', file);
  // formData.append('preview', file);

  return networkCall({
    method: 'post',
    url: `${config.chatOnlyBaseUrl}/tele/user/upload-prescription?consultationId=${consultationId}`,
    headers: {
      authorization: authToken,
      'content-type': 'multipart/form-data',
    },
    data: formData,
  });
}

/**
 * File upload for chat only upload
 * @param {*} file
 * @param {*} userId
 * @param {*} authToken
 * @returns
 */
export function uploadFileAttachmentChatOnly(file, consultationId, authToken) {
  const formData = new FormData();
  formData.append('file', file);
  // formData.append('preview', file);

  return networkCall({
    method: 'post',
    url: `${config.chatOnlyBaseUrl}/tele/user/upload-prescription?consultationId=${consultationId}`,
    headers: {
      authorization: authToken,
      'content-type': 'multipart/form-data',
    },
    data: formData,
  });
}

export function getConsultationSummary(consultationId, userId, authToken) {
  // console.log('consultationId', consultationId, userId, authToken);
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consultation/${consultationId}/summary/${userId}`,
    headers: { authorization: authToken },
  });
}

export function getSvg(svgUrl, authToken) {
  console.log('data is ', svgUrl, authToken);

  return networkCall({
    method: 'get',
    url: svgUrl,
    headers: { authorization: authToken },
    transformResponse: (response) => response, // Keep raw response
  });
}

export function consultAssistant(
  doctorInfo,
  consultRequestInfo,
  history,
  authToken,
  alternateNumber = null
) {
  const headers = {
    authorization: authToken,
  };
  const doctorId = doctorInfo.assistantId || doctorInfo.doctorId;
  const redirectToPaymentGateway = () => {
    // Not on phonepe in-app or some other error.
    // send to payment
    const queryParams = new URLSearchParams();
    queryParams.append('assistantId', doctorId);
    queryParams.append('amount', doctorInfo.finalFee);
    if (consultRequestInfo) {
      const { selectedIssuesIndex, relative } = consultRequestInfo;
      if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
        queryParams.append('issues', selectedIssuesIndex.toString());
      }
      if (relative && relative.id !== -1) {
        queryParams.append(
          'relativeId',
          relative.id !== -1 ? relative.id : undefined
        );
      }
      if (alternateNumber) {
        queryParams.append('alternateNumber', alternateNumber);
      }
    }
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/assistant?${queryParams.toString()}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  if (doctorInfo.finalFee.toString() === '0') {
    return redirectToPaymentGateway();
  }
  const params = {};
  params.assistantId = doctorId;
  params.amount = doctorInfo.finalFee;
  if (consultRequestInfo) {
    const { selectedIssuesIndex, relative } = consultRequestInfo;
    if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
      params.issues = selectedIssuesIndex.toString();
    }
    if (relative) {
      params.relativeId = relative.id !== -1 ? relative.id : undefined;
    }
  }
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/assistant`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function consultSpecialist(
  doctorInfo,
  consultRequestInfo,
  history,
  authToken,
  illnessId,
  replacePage,
  alternateNumber = null,
  amount,
  labOrderId,
  addressId,
  requestedCallType,
  lat = null,
  lng = null
) {
  // If offline, redirect to slot selection
  if (consultRequestInfo.consultType === 'offline') {
    console.log(consultRequestInfo);
    if (replacePage) {
      history.replace(`/consultation/appointment/${doctorInfo.doctorId}`, {
        consultRequestInfo,
        doctor: doctorInfo,
        consultationId: consultRequestInfo.consultationId,
        doctorName: doctorInfo.doctorName,
        latitude: consultRequestInfo.currentLat,
        longitude: consultRequestInfo.currentLong,
        illnessId: illnessId,
      });
    } else {
      history.push(`/consultation/appointment/${doctorInfo.doctorId}`, {
        consultRequestInfo,
        doctor: doctorInfo,
        consultationId: consultRequestInfo.consultationId,
        doctorName: doctorInfo.doctorName,
        latitude: consultRequestInfo.currentLat,
        longitude: consultRequestInfo.currentLong,
        illnessId: illnessId,
      });
    }
    return;
  }
  const headers = {
    authorization: authToken,
  };
  const redirectToPaymentGateway = () => {
    // Not on phonepe in-app or some other error.
    // send to payment
    const queryParams = new URLSearchParams();
    if (doctorInfo.doctorId) {
      queryParams.append('doctorId', doctorInfo.doctorId);
    }
    queryParams.append('amount', doctorInfo.finalFee);
    if (consultRequestInfo) {
      const { selectedIssuesIndex, relative, consultationId } =
        consultRequestInfo;
      if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
        queryParams.append('issues', selectedIssuesIndex.toString());
      }
      if (relative && relative.id && relative.id !== -1) {
        queryParams.append(
          'relativeId',
          relative.id !== -1 ? relative.id : undefined
        );
      }
      if (consultationId) {
        queryParams.append('consultationId', consultationId);
      }
    }
    if (amount > 0) {
      queryParams.append('amount', amount);
    }
    if (alternateNumber) {
      queryParams.append('alternateNumber', alternateNumber);
    }
    if (labOrderId) {
      queryParams.append('labOrderId', labOrderId);
    }
    if (addressId) {
      queryParams.append('addressId', addressId);
    }
    if (requestedCallType) {
      queryParams.append('requestedCallType', requestedCallType);
    }
    if (lat && lng) {
      queryParams.append('lat', lat);
      queryParams.append('lng', lng);
    }
    console.log(queryParams.toString());
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/specialist?${queryParams.toString()}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  if (doctorInfo.finalFee.toString() === '0') {
    return redirectToPaymentGateway();
  }
  const params = {};
  params.doctorId = doctorInfo.doctorId;
  params.amount = doctorInfo.finalFee;
  if (consultRequestInfo) {
    const { selectedIssuesIndex, relative, consultationId } =
      consultRequestInfo;
    if (selectedIssuesIndex && Array.isArray(selectedIssuesIndex)) {
      params.issues = selectedIssuesIndex.toString();
    }
    if (relative) {
      params.relativeId = relative.id !== -1 ? relative.id : undefined;
    }
    if (consultationId) {
      params.consultationId = consultationId;
    }
  }
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/specialist`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function fetchProfile(doctorId, lat, lng, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/doctors/${doctorId}/profile`,
    headers: { authorization: authToken },
    params: { lat, lng },
  });
}

export function fetchAssistantProfile(doctorId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/assistants/${doctorId}/profile`,
    headers: { authorization: authToken },
  });
}

export function trackConsultationPayment(transactionId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/payment/status`,
    headers: { authorization: authToken },
    data: { orderId: transactionId },
  });
}

export function preBookCheckOut(
  authToken,
  doctorId,
  dateKey,
  slotkey,
  preBookId,
  type,
  lat,
  long,
  illnessId,
  relativeId
) {
  console.log('preBookCheckOut', lat, long);
  const body = {
    doctorId: doctorId,
    dateKey: dateKey,
    slotKey: slotkey,
    preBooked: preBookId,
    type: type,
    latitude: lat,
    longitude: long,
    illnessId: illnessId,
  };
  if (relativeId) {
    body.relativeId = relativeId;
  }

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/prebook-aptmnt-checkout`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function storeConsultLocation(
  latitude,
  longitude,
  consultationId,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consultation/${consultationId}/location`,
    headers: { authorization: authToken },
    data: {
      latitude,
      longitude,
    },
  });
}

export function requestUrgentAssistance(consultationId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/chats/request-urgent-assistance`,
    headers: { authorization: authToken },
    data: { consultationId },
  });
}

export function pharmacyTeleConsultWithoutPrescription(
  addressId,
  relativeId,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/online/pharmacy-tele-consult`,
    headers: { authorization: authToken },
    data: { addressId, relativeId },
  });
}

export function pharmacyTeleConsultWithoutPrescriptionWithDigitizationId(
  digitisationId,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/online/pharmacy-tele-consult`,
    headers: { authorization: authToken },
    data: { digitisationId },
  });
}

export function requestDoctorCall(consultationId, requestType, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/connectionRequest/${consultationId}`,
    headers: { authorization: authToken },
    data: { requestType },
  });
}

export function fetchEmergencyContactsArray(data) {
  const { authToken, location } = data;
  const params = {
    relativeId:
      location.state.consultRequestInfo.relative.id === 'self'
        ? null
        : location.state.consultRequestInfo.relative.id,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/emergency-contact/get-info`,
    headers: { authorization: authToken },
    params,
  });
}

export function fetchRelativeId(data) {
  const { authToken, information } = data;
  const params = {
    scheduledConsultationId: information.location.state.scheduledConsultationId,
    consultationId: information.location.state.consultationId,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/emergency-contact/get-consultation-info`,
    headers: { authorization: authToken },
    params,
  });
}

export function fetchEmergencyContacts(data, relativeId) {
  const { authToken } = data;
  const params = {
    relativeId: relativeId,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/emergency-contact/get-info`,
    headers: { authorization: authToken },
    params,
  });
}

export function fetchActiveContact(data) {
  const { authToken, information } = data;
  const params = {
    scheduledConsultationId: information.location.state.scheduledConsultationId,
    consultationId: information.location.state.consultationId,
  };

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/emergency-contact/get-active-contact`,
    headers: { authorization: authToken },
    params,
  });
}

export function setContactActive(data) {
  const { information, contact } = data;
  const body = {
    contactId: contact.contactId,
    scheduledConsultationId:
      information.information.location.state.scheduledConsultationId,
    consultationId: information.information.location.state.consultationId,
  };

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/emergency-contact/set-contact-active`,
    headers: { authorization: information.authToken },
    data: body,
  });
}

export function deleteEmergencyContact(data, relativeId) {
  const { information, contact } = data;
  const body = {
    relativeId: relativeId,
    emergencyContact: contact.emergencyContact,
  };

  return networkCall({
    method: 'delete',
    url: `${config.apiBaseUrl}/emergency-contact/remove-info`,
    headers: { authorization: information.authToken },
    data: body,
  });
}

export function addEmergencyContact(data, value, relativeId) {
  const { information } = data;
  const body = {
    relativeId: relativeId,
    emergencyContactNo: Number(value.contact),
    name: value.name,
    relation: value.relation.toUpperCase(),
    consultationId: information.information.location.state.consultationId,
    scheduledConsultationId:
      information.information.location.state.scheduledConsultationId,
  };

  console.log(information.authToken, body, data, 'addEmergencyContact');
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/emergency-contact/save-info`,
    headers: { authorization: information.authToken },
    data: body,
  });
}

export function editEmergencyContact(data, value, relativeId) {
  console.log(data, value, 'editEmergencyContact');
  const { information, toEdit } = data;
  const body = {
    relativeId: relativeId,
    emergencyContactNo: Number(value.contact),
    name: value.name,
    relation: value.relation.toUpperCase(),
    contactId: toEdit,
  };

  return networkCall({
    method: 'put',
    url: `${config.apiBaseUrl}/emergency-contact/update-info`,
    headers: { authorization: information.authToken },
    data: body,
  });
}

export function markContactsInactive(data) {
  console.log(data, 'markeContactsInactive');
  const { authToken, information } = data;
  const body = {
    scheduledConsultationId: information.location.state.scheduledConsultationId,
    consultationId: information.location.state.consultationId,
  };

  return networkCall({
    method: 'put',
    url: `${config.apiBaseUrl}/emergency-contact/mark-all-inactive`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function getInvoice(
  doctorId,
  relativeId,
  dateKey,
  timingsId,
  type,
  lat,
  lng,
  authToken,
  illnessId,
  hcData
) {
  let body = {
    dateKey,
    timingsId,
    type,
    latitude: lat,
    longitude: lng,
    doctorId: doctorId || null,
  };

  // doctor booking or center booking with any doctor
  if (!doctorId) {
    body.centerId = hcData.id;
    body.centerRequest = 1;
  }

  if (illnessId) body.illnessId = illnessId;

  if (relativeId !== 'self') body.relativeId = relativeId;

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/checkout`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function fetchPreviousOfflineConsultation(relative = 'all', authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/offline-consults?relativeId=${relative}`,
    headers: { authorization: authToken },
  });
}

export function fetchPreviousOnlineConsultation(relative = 'all', authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/online-consults?relativeId=${relative}`,
    headers: { authorization: authToken },
  });
}

export function getAllRelatives(authToken, type) {
  console.log(type);

  if (type) {
    return networkCall({
      method: 'get',
      url: `${config.apiBaseUrl}/care/relatives?t=${type}`,
      headers: { authorization: store.getState().user.authToken },
    });
  } else {
    return networkCall({
      method: 'get',
      url: `${config.apiBaseUrl}/care/relatives`,
      headers: { authorization: store.getState().user.authToken },
    });
  }
}

export function fetchConsultationBlocker(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/blockers`,
    headers: { authorization: authToken },
  });
}

export function fetchVideoSessionDetails(sessionId, consultationId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/video-call/video-session-info?sessionId=${sessionId}&consultationId=${consultationId}`,
    headers: { authorization: authToken },
  });
}

export function onboardingRequest(body, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/care/onboarding-request-new`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function reimbursementRequestRegister(body, authToken) {
  const {
    date,
    relativeId,
    latitude,
    longitude,
    v,
    doctorName,
    centerName,
    location,
    phone,
    speciality,
  } = body;

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/reimbursements/consult`,
    headers: { authorization: authToken },
    params: {
      date,
      relativeId,
      latitude,
      longitude,
      v,
      doctorName,
      centerName,
      location,
      phone,
      speciality,
    },
  });
}

export function reimbursementDetailsForConsultation(
  reimbursementId,
  authToken
) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/reimbursements/${reimbursementId}/status`,
    headers: { authorization: authToken },
  });
}

export function reimbursementSubmit(body, reimbursementId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/reimbursements/${reimbursementId}/claim`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function reimbursementCancel(reimbursementId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/reimbursements/${reimbursementId}/cancel`,
    headers: { authorization: authToken },
    data: {},
  });
}

export function pendingRequestSubmit(body, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/prescription-blocker/submit/`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function onlineConsultationCheckout(
  body,
  authToken,
  isBfl = false,
  hasLabOrder
) {
  // Set the base URL for the checkout
  let url = `${config.apiBaseUrl}/opd/consult/online/checkout`;

  // Append the cf=labsConsultCheckout query parameter only if hasLabOrder is true
  if (hasLabOrder) {
    url += '?cf=labsConsultCheckout';
  }

  // Check if "isBfl" flag is true, append the bfl=true query parameter accordingly
  if (isBfl) {
    url += hasLabOrder ? '&bfl=true' : '?bfl=true';
  }

  return networkCall({
    method: 'post',
    url,
    headers: { authorization: authToken },
    data: body,
  });
}

export function fetchVideoSessionDetailByRoomId(roomId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/video-call/getRoomInfo?r=${roomId}`,
  });
}

export function updateRelative(body, authToken) {
  return networkCall({
    method: 'put',
    url: `${config.apiBaseUrl}/care/relatives`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function fetchScheduleCardDetail(scheduleId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/consultations/schedule-status/${scheduleId}`,
    headers: { authorization: authToken },
  });
}

export function getFreeAssessmentStatus(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/subscription/physio-assessment-status`,
    headers: { authorization: authToken },
  });
}

export function cancelScheduleCall(consultationId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/consultations/cancel-a-scheduled-call`,
    headers: { authorization: authToken },
    data: {
      consultationId,
      cancelledBy: 'user',
    },
  });
}

export function appointmentReconfirmation(requestId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/reconfirm`,
    headers: { authorization: authToken },
    data: { requestId },
  });
}

export function getAppointmentFinalStatus(requestId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/booking-details`,
    headers: { authorization: authToken },
  });
}

export function getCallFromExecutive(requestId, number, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/v2/initiate-call`,
    headers: { authorization: authToken },
    data: {
      requestId,
      executivePhoneNumber: number,
    },
  });
}

export function prescriptionBlockerPayment(
  requestId,
  amount,
  authToken,
  history,
  illnessId
) {
  const headers = {
    authorization: authToken,
  };
  const params = {
    amount,
    requestId,
    illnessId,
  };
  const redirectToPaymentGateway = () => {
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/consult/prescription-blocker/checkout?amount=${amount}&requestId=${requestId}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(`${config.apiBaseUrl}/opd/consult/prescription-blocker/checkout`, {
          headers,
          params,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `consultation/preview`,
                failurePage: `consultation/preview`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-text`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('consultation/preview', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('consultation/preview', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function payUpdatedAmountForAppointment(
  requestId,
  amount,
  authToken,
  history
) {
  const headers = {
    authorization: authToken,
  };
  const params = {
    amount,
    choice: 1,
  };
  const redirectToPaymentGateway = () => {
    window.location.assign(
      `${config.apiBaseUrl
      }/opd/v2/consult/request/${requestId}/confirm?choice=1&amount=${amount}&auth=${authToken.split(' ')[1]
      }`
    );
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(
          `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/confirm`,
          {
            headers,
            params,
          }
        )
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `consultation/preview`,
                failurePage: `consultation/preview`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-text`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('consultation/preview', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function fetchSlots(
  doctorId,
  authToken,
  gcds = true,
  relativeId = null
) {
  let url = `${config.apiBaseUrl}/opd/doctors/${doctorId}/schedule-consult-slots?gcds=${gcds}`;

  if (relativeId) {
    url += `&relativeId=${relativeId}`;
  }

  return networkCall({
    method: 'get',
    url,
    headers: { authorization: authToken },
  });
}

export function getScheduledConsultSummary(consultationId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consult/schedule-consult/${consultationId}/summary`,
    headers: { authorization: authToken },
  });
}

export function fetchMyOrders(relativeId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/online-consults-history?relativeId=${relativeId}`,
    headers: { authorization: authToken },
  });
}

export function getCancelReasons(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consult/schedule/cancel/reasons`,
    headers: { authorization: authToken },
  });
}

export function cancelScheduledConsult(
  scheduledConsultationId,
  cancelReason,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/schedule-consult/cancel`,
    headers: { authorization: authToken },
    data: { scheduledConsultationId, cancelReason },
  });
}

export function bookScheduledConsultation(
  doctorId,
  requestedDate,
  requestedSlot,
  relativeId,
  finalFee,
  history,
  authToken,
  requestedCallType,
  addressId,
  lat = null,
  lng = null
) {
  const headers = {
    authorization: authToken,
  };
  let url = `${config.apiBaseUrl
    }/opd/consult/schedule-consult?doctorId=${doctorId}&requestedDate=${requestedDate}&requestedSlot=${requestedSlot}&auth=${authToken.split(' ')[1]
    }`;
  if (relativeId !== 'self') url += `&relativeId=${relativeId}`;
  if (requestedCallType) url += `&requestedCallType=${requestedCallType}`;
  if (addressId) url += `&addressId=${addressId}`;
  if (lat && lng) url += `&lat=${lat}&lng=${lng}`;

  const redirectToPaymentGateway = () => {
    return window.location.assign(url);
  };
  if (finalFee === true || finalFee.toString() === '0') {
    return redirectToPaymentGateway();
  }
  url += `&amount=${finalFee}`; // not free

  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(url, {
          headers,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'consultation',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-test`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function fetchInconvenientRequest(requestId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/hospital/fetch-inconvenient-request/${requestId}`,
    headers: { authorization: authToken },
  });
}

export function submitInconvenientRequest(requestId, reason, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/hospital/mark-inconvenient/${requestId}`,
    headers: { authorization: authToken },
    data: { reason },
  });
}
export function getMajorConcernIssues(verticalId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consultationIssues?verticalId=${verticalId}`,
    headers: { authorization: authToken },
  });
}

export function getMinorConcernIssues(verticalId, issueId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/getConsultationSubIssues?issueId=${issueId}&verticalId=${verticalId}`,
    headers: { authorization: authToken },
  });
}

export function getPreBookedSteps(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/prebooked-processes`,
    headers: { authorization: authToken },
  });
}

export function logHealthConcernIssues(
  authToken,
  verticalId,
  issueId,
  subIssueId,
  otherDescription
) {
  if (subIssueId && subIssueId.length) {
    subIssueId = JSON.stringify(subIssueId);
  } else {
    subIssueId = '';
  }

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/logHealthIssues`,
    headers: { authorization: authToken },
    data: {
      verticalId,
      issueId,
      // subIssueId, otherDescription
    },
  });
}

export function getLanguages(authToken, verticalId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/search/languages?limit=3&verticalId=${verticalId}`,
    headers: { authorization: authToken },
  });
}

// export function getPopularLanguages(authToken) {
//   const headers = {
//     authorization: authToken,
//   }

//   return axios.get(`${config.apiBaseUrl}/opd/search/most-preferred-languages?limit=3`, {
//     headers,
//   })
//     .then((resp) => {
//       console.log(resp.data, 'resp');
//       const responseBody = resp.data;
//       if (resp.status === 200) {
//         return responseBody;
//       } else {
//         const err = new Error('Invalid response');
//         err.data = responseBody;
//         throw err;
//       }
//     })
//     .catch((err) => {
//       showError(err);
//       throw err;
//     });
// }

export function getRefundDetails({
  authToken,
  consultationId,
  scheduledConsultationId,
}) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consult/refund-details`,
    headers: { authorization: authToken },
    params: {
      consultationId,
      scheduledConsultationId,
    },
  });
}

export function refundStatusOffline(requestId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/care/refund-details`,
    headers: { authorization: authToken },
    data: { requestId },
  });
}

export function confirmPrebookingAppointment(
  doctorId,
  amount,
  verticalId,
  lat,
  long,
  dateKey,
  illnessId,
  slotKey,
  prebookedId,
  bookingSource,
  authToken,
  history,
  relativeId
) {
  const headers = {
    authorization: authToken,
  };
  const params = {
    doctorId,
    amount,
    verticalId,
    lat,
    long,
    dateKey,
    illnessId,
    slotKey,
    prebookedId,
    bookingSource,
  };
  let url = `${config.apiBaseUrl}/opd/consult/offline?`;
  if (doctorId) url += `doctorId=${doctorId}&`;
  if (amount) url += `amount=${amount}&`;
  if (verticalId) url += `v=${verticalId}&`;
  if (lat) url += `latitude=${lat}&`;
  if (long) url += `longitude=${long}&`;
  if (dateKey) url += `dateKey=${dateKey}&`;
  if (illnessId) url += `illnessId=${illnessId}&`;
  if (slotKey)
    url += `slotKey=${slotKey.hour}%3A${slotKey.min}%3A00%20${slotKey.modifier}&`;
  if (prebookedId) url += `preBooked=${prebookedId}&`;
  if (bookingSource) url += `bookingSource=${bookingSource}&`;
  if (relativeId) url += `relativeId=${relativeId}&`;
  if (authToken) url += `auth=${authToken.split(' ')[1]}&`;

  const redirectToPaymentGateway = () => {
    window.location.assign(url);
  };
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(
          `${config.apiBaseUrl}/opd/v2/consult/request/${doctorId}/confirm`,
          {
            headers,
            params,
          }
        )
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentPage')) {
              history.replace('/', {
                orderType: 'prebook appointment',
                transactionId: responseBody.transactionId,
                successPage: `home/consult`,
                failurePage: `home/consult`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}lab-text`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Prebook Appointment',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('home/consult', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function getAddress(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/labs-v2/address`,
    headers: { authorization: authToken },
  });
}

export function submitAppointmentPreferenceOptionService({
  authToken,
  optionId,
  requestId,
}) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/consult/appointment-preference-option`,
    headers: { authorization: authToken },
    data: {
      optionId,
      requestId,
    },
  });
}

export function fetchAppointmentPreferenceOptionsService({ authToken }) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/consult/get-all-appointment-preference-options`,
    headers: { authorization: authToken },
  });
}

export function removeAddress(addressId, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/labs-v2/address/remove`,
    headers: { authorization: authToken },
    data: { addressId },
  });
}

export function editAddressConsultation(addressDetails, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/labs-v2/address/edit`,
    headers: { authorization: authToken },
    data: addressDetails,
  });
}

export function addNewAddressOnline(body, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/labs-v2/address`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function ediUserUHID(doctorId, relativeId, userUhid, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/v2/center/user-uhid`,
    headers: { Authorization: authToken },
    data: {
      doctorId,
      relativeId: relativeId === 'self' ? null : relativeId,
      userUhid,
    },
  });
}

export function fetchUserUHIDList(doctorId, phone, authToken) {
  console.log('fetchUserUHIDList', doctorId, phone);
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/center/user-uhid-list`,
    headers: { Authorization: authToken },
    params: {
      doctorId: doctorId,
      phone: phone,
    },
  });
}

export function fetchFeedbackConsultationInfo({ consultationId, rating }) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/version/online/feedback`,
    params: { consultationId },
  });
}

export function fetchFeedbackReasons({ type, rating }) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/version/online/feedback/reasons`,
    params: { type, rating },
  });
}

export function addFeedback({ consultationId, feedbacks }) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/version/online/link/feedback`,
    data: { consultationId, feedbacks },
  });
}

export function fetchFeedbackRatings({ consultationId }) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/version/online/feedback/rating`,
    params: { consultationId },
  });
}

export function getTatvaConsultationSummary(consultationId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.pbChatBaseUrl}/initiate-chat?consultationId=${consultationId}`,
    headers: { Authorization: authToken },
  }).then((responseBody) => responseBody?.consultations[0]);
}

export function fetchCentresForClinicConsult(authToken, lat, lng, page) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/getAllcenters?lat=${lat}&lng=${lng}&page=${page}&v=3&prebooked=0`,
    headers: { authorization: authToken },
  });
}

export function bookOfflineConsultForCenter(
  authToken,
  centerId,
  dateKey,
  timingsId,
  latitude,
  longitude,
  relativeId,
  v
) {
  let body = {
    centerId,
    dateKey,
    timingsId,
    latitude,
    longitude,
    v,
    version: config.v2,
  };

  if (relativeId !== 'self') body.relativeId = relativeId;

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/centerRequest`,
    headers: { authorization: authToken },
    data: body,
  });
}

export function slotsForCenterAnyDoctor(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/doctors/null/slots?centerRequest=1`,
    headers: { authorization: authToken },
  });
}

export function getPrebookSteps(authToken, doctorHash, version) {
  const queryParams = new URLSearchParams();
  if (version) {
    queryParams.append('v', 1800009616);
  }
  if (doctorHash) {
    queryParams.append('doctorHash', doctorHash);
  }

  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl
      }/opd/v2/consult/request/prebooked-processes?${queryParams.toString()}`,
    headers: { authorization: authToken },
  });
}

export function checkIn(authToken, requestId, checkInType) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/update-prebook-status`,
    headers: { authorization: authToken },
    data: { status: checkInType },
  });
}

export function fetchSamplePrescription(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/getUploadPrescriptionGuidelines`,
    headers: { authorization: authToken },
  });
}

export function uploadRxOffline(authToken, userId, data) {
  console.log('uploadRxOffline', data);
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/opd/users/${userId}/upload`,
    headers: { authorization: authToken },
    data,
  });
}

export function fetchOfflineQuery(authToken, requestId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/${requestId}/fetch-offline-query`,
    headers: { authorization: authToken },
  });
}

export function fetchOfflineQueryHistory(authToken, digitisationId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/${digitisationId}/fetch-offline-deficiency-history`,
    headers: { authorization: authToken },
  });
}
export function removeUploadedRxFile(authToken, uploadId) {
  console.log('auth', authToken);
  return networkCall({
    method: 'delete',
    url: `${config.apiBaseUrl}/opd/consult/prescription-blocker/prescriptions`,
    headers: { authorization: authToken },
    data: { uploadId },
  });
}

export function getPrebookedProcesses(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/prebooked-processes`,
    headers: { authorization: authToken },
  });
}

export function getPrebookConsultDetails(authToken, doctorId) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/opd/v2/consult/request/prebook-consult-details?doctorHash=${doctorId}`,
    headers: { authorization: authToken },
  });
}
